import { useService } from 'react-reservice';

import { EMainProductCodeType, EMarketTimeManagementType } from '@webtrader/core/api/config/company';
import { isPROD, isSIT } from '@webtrader/core/configs';
import { ILangOption, LANG_OPTIONS, LANG_OPTIONS_MAP, TLang } from '@webtrader/core/i18n';
import { DEFAULT_CANDLE_STICK_COLORS_RGB } from '@webtrader/core/themes/colors';
import { validateCompanyAccess } from '@webtrader/core/validators/company';

import { TENANT_THEMES_MAP } from './constants';
import companyService from './service';

export const useCompany = () => {
  return useService(companyService, (state) => state);
};

export const useCompanyIntegration = () =>
  useService(companyService, ({ company }) => {
    const { mainProductCodeList, currentMainProductCode } = company || {};

    return currentMainProductCode || mainProductCodeList?.[0] || EMainProductCodeType.METATRADER4;
  });

export const useCompanyIntegrationType = () =>
  useService(companyService, ({ company }) => {
    const { mainProductCodeList = [], currentMainProductCode } = company || {};

    const mainProductCode = currentMainProductCode || mainProductCodeList?.[0] || EMainProductCodeType.METATRADER4;
    const isCentroid = mainProductCode === EMainProductCodeType.CENTROID;
    const isMT5 = mainProductCode === EMainProductCodeType.METATRADER5;
    const isMT4 = mainProductCode === EMainProductCodeType.METATRADER4;

    return { isMT5, isMT4, isCentroid };
  });

export const useCompanyIsMultiServerEnabled = () =>
  useService(companyService, ({ company }) => company?.multiServerEnabled || false);

export const useCompanyAccess = () => {
  return useService(companyService, ({ access }) => access);
};

export const useCompanyMarketTimeIsServer = () =>
  useService(companyService, ({ company }) => company?.marketTimeManagement === EMarketTimeManagementType.SERVER);

export const useCompanyIntegrationLabel = () => {
  const integrationLabel: Partial<Record<EMainProductCodeType, string>> = {
    [EMainProductCodeType.METATRADER4]: 'MT4',
    [EMainProductCodeType.METATRADER5]: 'MT5',
    [EMainProductCodeType.CENTROID]: 'CENTROID'
  };
  const companyIntegration = useCompanyIntegration();

  return integrationLabel[companyIntegration];
};

export const useCompanyHideMarginRequirement = () => {
  return useService(companyService, ({ company }) => {
    if (company) {
      const { hasEstimatedMarginRequired } = validateCompanyAccess(company);
      return !hasEstimatedMarginRequired;
    }
    return false;
  });
};

export const useCompanyHideBackofficeMetatraderDataColumns = () => {
  const tenantIds = ['klayfx', 'yaprime', 'nxgmarkets'];

  return useService(companyService, ({ company }) => {
    if (company && company.tenantId) {
      return tenantIds.includes(company.tenantId);
    }
    return false;
  });
};

export const useCompanyArabicLanguageAvailable = (environment?: string) => {
  return useService(companyService, ({ company }) => {
    return isSIT(environment) || company?.tenantId === 'fintech';
  });
};

export const useCompanyShowTradingViewChart = () => {
  return useService(companyService, ({ company }) => {
    if (company) {
      const { hasTradingViewChart } = validateCompanyAccess(company);
      return hasTradingViewChart;
    }
    return false;
  });
};

export const useCompanyAppsFlyerTracking = () => {
  const tenantIds = ['lirunex', 'hantec'];

  return useService(companyService, ({ company }) => {
    if (company && company.tenantId) {
      return tenantIds.includes(company.tenantId);
    }
    return false;
  });
};

export const useCompanyFBSDKTracking = (environment?: string) => {
  const tenantIds = ['lirunex'];

  return useService(companyService, ({ company }) => {
    if (company && company.tenantId && isPROD(environment)) {
      return tenantIds.includes(company.tenantId);
    }
    return false;
  });
};

export const useCompanyAdjustTracking = () => {
  const tenantIds = ['lirunex'];

  return useService(companyService, ({ company }) => {
    if (company && company.tenantId) {
      return tenantIds.includes(company.tenantId);
    }
    return false;
  });
};

export const useCompanyLanguageOptions = () => {
  const languageOptions = useService(companyService, ({ company }) => {
    let langCodes = company?.supportedLanguages;
    // WT-8792: Temporary hardcode until blueberry or hantec updates their supported languages
    if (!company?.supportedLanguages) {
      if (company?.tenantId === 'blueberry') {
        langCodes = ['en_GB', 'zh_CN', 'zh_TW'];
      } else if (company?.tenantId === 'hantec') {
        // WT-8803: Hardcode language sequence for hantec
        // hantec (in prod/uat)
        langCodes = ['en_GB', 'es_ES', 'ja_JP', 'ko_KR', 'pt_BR', 'zh_CN', 'zh_TW', 'th_TH'];
      }
    }

    if (!langCodes) {
      return LANG_OPTIONS;
    }

    return langCodes.reduce((prev: ILangOption[], langCode: TLang) => {
      if (!LANG_OPTIONS_MAP[langCode]) {
        return prev;
      }
      prev.push(LANG_OPTIONS_MAP[langCode]);
      return prev;
    }, []);
  });

  return languageOptions;
};

/** Returns whether to display notification alerts after order actions (placing/editing orders/close orders) */
export const useCompanyShowNotificationOnOrderActions = () => {
  const { isCentroid } = useCompanyIntegrationType();
  return !isCentroid;
};

/** For Centroid integration, notifications are handled via websocket messages */
export const useCompanyShowNotificationOnSocketMessage = () => {
  const { isCentroid } = useCompanyIntegrationType();
  return isCentroid;
};

export const useTimezone = () => {
  return useService(companyService, ({ company, loading }) => {
    const { serverTimezone, marketTimeManagement } = company || {};

    const serverConfigs = {
      offset: serverTimezone as number
    };

    const configs = marketTimeManagement === EMarketTimeManagementType.SERVER ? serverConfigs : undefined;

    return { configs, loading };
  });
};

export const useCompanyCustomThemeTemplate = () => {
  return useService(companyService, ({ company }) => {
    const themeTemplate = company?.tenantId
      ? TENANT_THEMES_MAP[company.tenantId] || TENANT_THEMES_MAP.default
      : TENANT_THEMES_MAP.default;
    return { themeTemplate };
  });
};

export const useCompanyDefaultCandlestickColors = () => {
  return useService(companyService, ({ company }) => ({
    positive: company?.profitColourCode || DEFAULT_CANDLE_STICK_COLORS_RGB.positive,
    negative: company?.lossColourCode || DEFAULT_CANDLE_STICK_COLORS_RGB.negative
  }));
};

export { companyService };
